import React, {useEffect, useState, useRef, useContext} from 'react'
import {
  Container,
  Button,
  Grid,
  Typography,
  Box,
  Card,
  TextField,
  CircularProgress,
} from '@material-ui/core'
import {Link} from 'react-router-dom'
import {Mutation, Query} from 'react-apollo'
import {barcodeSettingQuery} from '../../graphql/barcode/queries/barcodeSettingsQuery'
import {sendPdfRequest} from '../../graphql/barcode/mutations/sendPdfRequestMutation'
import {useTranslation} from 'react-i18next'
import useStyles from './BarcodeWizardGenerateAutomatic.styles'
import BarcodeNavHeaderButtons from '../BarcodeNavHeaderButtons'
import BarcodeLoadingScreen from '../BarcodeLoadingScreen'
import BarcodeWizardPreview from '../BarcodeWizardPreview'
import {BarcodeLabelRecordContext} from '../../context/BarcodeLabelRecord'
import {BarcodeSheetRecordContext} from '../../context/BarcodeSheetRecord'
import {ToastMessageContext} from '../../context/ToastMessage'
import {UserContext} from '../../context/UserContext'
import {validateBarcodePermission} from '../../modules/common/utils/permissions'

function BarcodeWizardGenerateAutomatic(props) {
  const toast = useContext(ToastMessageContext)
  const classes = useStyles()
  const {history} = props
  const {t} = useTranslation()
  const {barcodeLabelRecord} = useContext(BarcodeLabelRecordContext)
  const {barcodeSheetRecord} = useContext(BarcodeSheetRecordContext)
  const [isNewDownload, setDownload] = useState(false)
  const userId = parseInt(window.localStorage.getItem('user_id'))
  const userEmail = window.localStorage.getItem('user_email')
  const accessToken = JSON.parse(window.localStorage.getItem('token'))
  const currentUser = React.useContext(UserContext)

  const [formValues, setFormValues] = useState({
    totalGenerate: '1',
    startPosition: '1',
    totalRepeat: '1',
    maxPosition: '1',
  })
  const [barcodeSettingRecord, setBarcodeSettingRecord] = useState('')
  const refBtnGenerate = useRef()
  useEffect(() => {
    if (!!!barcodeLabelRecord || !!!barcodeSheetRecord) {
      history.replace({
        pathname: 'barcode-wizard',
      })
    }
  }, [barcodeLabelRecord, barcodeSheetRecord, history])

  useEffect(() => {
    if (window !== window.parent) {
      window.parent.postMessage('scrollTop', process.env.REACT_APP_BARCODE_WINDOW_URL)
    } else {
      window.scrollTo(0, 0)
    }
  }, [])

  const handleChange = textField => event => {
    setFormValues({
      ...formValues,
      [textField]: event.target.value,
    })
  }

  function changePosition(value, maxPosition) {
    setFormValues({
      ...formValues,
      startPosition: value,
      maxPosition: maxPosition,
    })
  }

  function checkValidity() {
    let errorMessage = ''

    if (formValues.totalRepeat === '') {
      errorMessage = t('barcode.wizard.generate-automatic.repeat-label-blank')
    } else if (formValues.totalGenerate === '') {
      errorMessage = t('barcode.wizard.generate-automatic.label-blank')
    } else if (formValues.startPosition === '') {
      errorMessage = t('barcode.wizard.generate-automatic.start-blank')
    } else if (parseInt(formValues.totalRepeat) > 5) {
      errorMessage = t('barcode.wizard.generate-automatic.repeat-label-equal')
    } else if (parseInt(formValues.totalRepeat) < 1) {
      errorMessage = t('barcode.wizard.generate-automatic.repeat-label-less-than')
    } else if (parseInt(formValues.totalGenerate) < 1) {
      errorMessage = t('barcode.wizard.generate-automatic.label-less-than')
    } else if (parseInt(formValues.startPosition) < 1) {
      errorMessage = t('barcode.wizard.generate-automatic.start-less-than')
    } else if (
      parseInt(formValues.startPosition) >
      barcodeSheetRecord.rows * barcodeSheetRecord.columns
    ) {
      errorMessage = t('barcode.wizard.generate-automatic.start-out-range')
    }

    if (errorMessage !== '') {
      toast.popMessage(errorMessage, 'error', refBtnGenerate.current)
      return false
    }
    return true
  }

  function generatePdf(sendPdfRequest) {
    if(!validateBarcodePermission(currentUser, "can_generate")){
      return;
    }
    if (checkValidity()) {
      setDownload(true)
      sendPdfRequest({
        variables: {
          id: barcodeSettingRecord.id,
          accountId: barcodeSettingRecord.accountId,
          userId: userId,
          token: accessToken,
          userEmail: userEmail,
          barcodeLabelId: parseInt(barcodeSettingRecord.barcodeLabelId),
          output: formValues.output,
          digits: parseInt(formValues.digits),
          prefix: formValues.prefix,
          labelsToBeGenerated: parseInt(formValues.totalGenerate),
          totalRepeat: parseInt(formValues.totalRepeat),
          startPosition: parseInt(formValues.startPosition),
          dataSource: props.showTable === 'automated' ? 'automated' : 'from_objects',
          fieldId: props.showTable === 'automated' ? null : parseInt(formValues.field),
          entityId: props.showTable === 'automated' ? null : parseInt(formValues.group),
        },
      })
    }
  }

  return (
    <div>
      <BarcodeNavHeaderButtons
        previous={(props.location.state || {}).previous}
        template={barcodeSettingRecord}
      />

      <Grid container>
        <Grid item xs={6}>
          <Container>
            <Card>
              <Grid container>
                <Grid container item xs={10} justify="flex-start">
                  <Typography variant="h5">
                    <div className={classes.titleStyle}>
                      {t('barcode.wizard.generate-automatic.selected-sheet')}
                    </div>
                  </Typography>
                </Grid>
                <Grid container item xs={2} justify="flex-end" margin="auto">
                  <Box marginTop={3} marginRight={3}>
                    <Link to="barcode-wizard">{t('barcode.wizard.generate-automatic.change')}</Link>
                  </Box>
                </Grid>
                <Grid container justify="flex-start">
                  <div className={classes.descriptionStyle}>
                    {t('barcode.wizard.generate-automatic.sheet-template')}
                  </div>
                </Grid>
                <Grid container justify="flex-start">
                  <div className={classes.informationStyle}>
                    {barcodeSheetRecord
                      ? barcodeSheetRecord.name
                      : t('barcode.wizard.generate-automatic.sheet-name')}
                  </div>
                </Grid>
              </Grid>
            </Card>
            <Box marginTop={5} />
            <Card>
              <Grid container>
                <Grid container item xs={10} justify="flex-start">
                  <Typography variant="h5">
                    <div className={classes.titleStyle}>
                      {t('barcode.wizard.generate-automatic.selected-label')}
                    </div>
                  </Typography>
                </Grid>
                <Grid container item xs={2} justify="flex-end" margin="auto">
                  <Box marginTop={3} marginRight={3}>
                    <Link to="barcode-wizard">{t('barcode.wizard.generate-automatic.change')}</Link>
                  </Box>
                </Grid>
                <Grid container>
                  <div className={classes.descriptionStyle}>
                    {t('barcode.wizard.generate-automatic.label-template')}
                  </div>
                </Grid>
                <Grid container>
                  <div className={classes.informationStyle}>
                    {barcodeLabelRecord
                      ? barcodeLabelRecord.name
                      : t('barcode.wizard.generate-automatic.label-name')}
                  </div>
                </Grid>
              </Grid>
            </Card>
            <Box marginTop={5} />

            <Query
              query={barcodeSettingQuery}
              variables={{id: ((props.location.state || {}).barcodeData || {}).id}}
            >
              {({loading, error, data}) => {
                if (loading)
                  return (
                    <div>
                      <BarcodeLoadingScreen size={50} />
                    </div>
                  )
                if (error)
                  return (
                    <div>
                      {props.history.push({
                        pathname: 'pageNotFound',
                      })}
                    </div>
                  )
                setBarcodeSettingRecord(data.barcodeSetting[0])

                return (
                  <Card>
                    <Grid container>
                      <Grid container item xs={10} justify="flex-start">
                        <Typography variant="h5">
                          <div className={classes.titleStyle}>
                            {t('barcode.wizard.generate-automatic.generate-title')}
                          </div>
                        </Typography>
                      </Grid>
                      <Grid container item xs={2} justify="flex-end" margin="auto">
                        <Box marginTop={3} marginRight={3}>
                          <Link to="barcode-wizard-auto-form">
                            {t('barcode.wizard.generate-automatic.change')}
                          </Link>
                        </Box>
                      </Grid>
                    </Grid>

                    <Grid container>
                      <Grid container item xs={6}>
                        <Grid container>
                          <div className={classes.descriptionStyle}>
                            {t('barcode.wizard.generate-automatic.name')}
                          </div>
                        </Grid>

                        <div className={classes.informationStyle}>{barcodeSettingRecord.name}</div>
                      </Grid>

                      <Grid container item xs={6}>
                        <Grid container>
                          <div className={classes.descriptionStyle}>
                            {t('barcode.wizard.generate-automatic.type')}
                          </div>
                        </Grid>

                        <div className={classes.informationStyle}>
                          {barcodeSettingRecord.barcodeType
                            ? barcodeSettingRecord.barcodeType
                            : t('barcode.wizard.generate-automatic.none')}
                        </div>
                      </Grid>

                      <Grid container item xs={6}>
                        <Grid container>
                          <div className={classes.descriptionStyle}>
                            {t('barcode.wizard.generate-automatic.label-template')}
                          </div>
                        </Grid>

                        <div className={classes.informationStyle}>
                          {barcodeSettingRecord.label
                            ? barcodeSettingRecord.label.name
                            : t('barcode.wizard.generate-automatic.name')}
                        </div>
                      </Grid>

                      <Grid container item xs={6}>
                        <Grid container>
                          <div className={classes.descriptionStyle}>
                            {t('barcode.wizard.generate-automatic.digits')}
                          </div>
                        </Grid>

                        <div className={classes.informationStyle}>
                          {barcodeSettingRecord.digits}
                        </div>
                      </Grid>

                      <Grid container item xs={6}>
                        <Grid container>
                          <div className={classes.descriptionStyle}>
                            {t('barcode.wizard.generate-automatic.start-number')}
                          </div>
                        </Grid>

                        <div className={classes.informationStyle}>
                          {barcodeSettingRecord.startNumber}
                        </div>
                      </Grid>

                      <Grid container item xs={6}>
                        <Grid container>
                          <div className={classes.descriptionStyle}>
                            {t('barcode.wizard.generate-automatic.prefix')}
                          </div>
                        </Grid>

                        <div className={classes.informationStyle}>
                          {barcodeSettingRecord.prefix
                            ? barcodeSettingRecord.prefix
                            : t('barcode.wizard.generate-automatic.none')}
                        </div>
                      </Grid>

                      <Grid container item xs={6}>
                        <Grid container>
                          <div className={classes.descriptionStyle}>
                            {t('barcode.wizard.generate-automatic.last-code')}
                          </div>
                        </Grid>

                        <div className={classes.informationStyle}>
                          {barcodeSettingRecord.lastPrinted}
                        </div>
                      </Grid>

                      <Grid container item xs={6}>
                        <Grid container>
                          <div className={classes.descriptionStyle}>
                            {t('barcode.wizard.generate-automatic.next-code')}
                          </div>
                        </Grid>

                        <div className={classes.informationStyle}>
                          {barcodeSettingRecord.nextToPrint}
                        </div>
                      </Grid>

                      <Grid container item xs={6}>
                        <Grid container>
                          <div className={classes.descriptionStyle}>
                            {t('barcode.wizard.generate-automatic.output')}
                          </div>
                        </Grid>

                        <div className={classes.informationStyle}>
                          {barcodeSettingRecord.output && barcodeSettingRecord.output.toUpperCase()}
                        </div>
                      </Grid>

                      <Grid container item xs={6} justify="flex-end" margin="auto">
                        <Grid container />
                      </Grid>
                    </Grid>
                  </Card>
                )
              }}
            </Query>
            <Grid container className={classes.textFieldStyle}>
              <Grid container item xs={7}>
                <TextField
                  id="total-generate"
                  label={t('barcode.wizard.generate-automatic.how-many-labels')}
                  value={formValues.totalGenerate}
                  fullWidth
                  type="number"
                  variant="outlined"
                  onChange={handleChange('totalGenerate')}
                />
              </Grid>
              <Grid container item xs={1} />
              <Grid
                container
                item
                xs={4}
                justify="flex-end"
                hidden={barcodeSettingRecord.output === 'PNG'}
              >
                <TextField
                  id="start-position"
                  label={t('barcode.wizard.generate-automatic.start-position-sheet')}
                  value={formValues.startPosition}
                  fullWidth
                  type="number"
                  variant="outlined"
                  onChange={handleChange('startPosition')}
                />
              </Grid>

              <Grid
                container
                item
                xs={12}
                className={classes.textFieldStyle}
                hidden={barcodeSettingRecord.output === 'PNG'}
              >
                <TextField
                  id="total-repeat"
                  label={t('barcode.wizard.generate-automatic.how-many-repeat')}
                  value={formValues.totalRepeat}
                  fullWidth
                  type="number"
                  variant="outlined"
                  onChange={handleChange('totalRepeat')}
                />
              </Grid>
              <Grid container item xs={12} className={classes.textFieldStyle} justify="flex-end">
                <Mutation
                  mutation={sendPdfRequest}
                  refetchQueries={() => [
                    {
                      query: barcodeSettingQuery,
                      variables: {id: (props.location.state || {}).barcodeData.id},
                    },
                  ]}
                  awaitRefetchQueries
                >
                  {(sendPdfRequest, {loading, data}) => {
                    if (data && isNewDownload) {
                      setDownload(false)
                      if (
                        data.sendPdfRequest.errors.length === 1 &&
                        data.sendPdfRequest.pdfData.location.length > 0
                      ) {
                        window.location.href = `${data.sendPdfRequest.pdfData.location}`
                      } else {
                        toast.popMessage(
                          data.sendPdfRequest.errors[0],
                          'error',
                          refBtnGenerate.current
                        )
                      }
                    }

                    return (
                      <Button
                        onClick={e => generatePdf(sendPdfRequest)}
                        value="generate"
                        variant="contained"
                        color="primary"
                        disabled = {!validateBarcodePermission(currentUser, "can_generate")}
                        ref={refBtnGenerate}
                      >
                        {loading ? (
                          <div>
                            {t('barcode.wizard.generate-automatic.generating')}
                            <CircularProgress size={20} color="secondary" />
                          </div>
                        ) : (
                          t('barcode.wizard.generate-automatic.finish-generate')
                        )}
                      </Button>
                    )
                  }}
                </Mutation>
              </Grid>
            </Grid>
            <Box marginTop={10} />
          </Container>
        </Grid>

        <Grid item xs={6}>
          <BarcodeWizardPreview
            page="generate"
            barcodeStartPosition={formValues.startPosition}
            changePosition={changePosition}
            viewPNG={barcodeSettingRecord.output === 'PNG'}
          />
        </Grid>
      </Grid>
    </div>
  )
}

export default BarcodeWizardGenerateAutomatic
